import { useEffect, useRef, useState, type RefObject } from "react";

const STORAGE_KEY = "otus:grid-columns";
const STEP_PX = 30;
export const MIN_COLUMNS = 3;
export const MAX_COLUMNS = 6;

function clamp(n: number) {
  return Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, n));
}

function readStored(): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? clamp(n) : null;
}

function isTouchDevice() {
  return typeof window !== "undefined" && "ontouchstart" in window;
}

function distance(a: Touch, b: Touch) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/**
 * Pinch-to-zoom column overrides for the photo grid.
 *
 * - Only active on touch devices.
 * - Starts from `fallback` (the width-responsive column count) until the user
 *   pinches, after which the chosen count sticks and is persisted.
 * - Spread (pinch out) -> fewer columns (bigger thumbnails).
 *   Squeeze (pinch in) -> more columns (smaller thumbnails).
 */
export function usePinchColumns(
  scrollRef: RefObject<HTMLElement | null>,
  fallback: number,
) {
  const [override, setOverride] = useState<number | null>(() => readStored());
  const baselineRef = useRef<number | null>(null);
  const currentRef = useRef<number>(override ?? fallback);

  useEffect(() => {
    if (!isTouchDevice()) return;
    const el = scrollRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      baselineRef.current = distance(e.touches[0], e.touches[1]);
      currentRef.current = override ?? fallback;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || baselineRef.current == null) return;
      // Suppress the browser's native pinch-zoom on the grid
      e.preventDefault();

      const d = distance(e.touches[0], e.touches[1]);
      const delta = d - baselineRef.current;
      if (Math.abs(delta) < STEP_PX) return;

      const steps = Math.trunc(delta / STEP_PX);
      // Spread (positive delta) -> fewer columns
      const next = clamp(currentRef.current - steps);
      if (next !== currentRef.current) {
        currentRef.current = next;
        setOverride(next);
        window.localStorage.setItem(STORAGE_KEY, String(next));
      }
      // Reset baseline by the consumed steps so further movement keeps stepping
      baselineRef.current = baselineRef.current + steps * STEP_PX;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) baselineRef.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [scrollRef, fallback, override]);

  return override ?? fallback;
}
